import { type FirebaseApp, type FirebaseOptions, initializeApp } from 'firebase/app';
import {
  confirmPasswordReset as fbConfirmPasswordReset,
  sendPasswordResetEmail as fbSendPasswordResetEmail,
  verifyPasswordResetCode as fbVerifyPasswordResetCode,
  getAuth,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';

import type {
  BaseFbAuthProps,
  ConfirmPasswordResetProps,
  FetchAuthTokenProps,
  LoginProps,
  SendPasswordResetProps,
  VerifyPasswordResetCodeProps,
} from './types.ts';

export function initializeFirebase(config: FirebaseOptions) {
  try {
    return initializeApp(config);
  } catch (e) {
    throw new Error('Could not initialize firebase:', e);
  }
}

export function getFirebaseAuth(app: FirebaseApp) {
  return getAuth(app);
}

export function login({ auth, email, password }: LoginProps) {
  return signInWithEmailAndPassword(auth, email, password);
}

export function logout({ auth }: BaseFbAuthProps): Promise<void> {
  return signOut(auth);
}

export async function fetchAuthToken({ auth, forceRefresh = false }: FetchAuthTokenProps): Promise<string | null> {
  const currentUser = auth.currentUser;

  if (!currentUser) {
    return null;
  }

  return await currentUser.getIdToken(forceRefresh);
}

export async function sendPasswordResetEmail({ auth, email, url }: SendPasswordResetProps): Promise<void> {
  return await fbSendPasswordResetEmail(auth, email, { url });
}

export async function verifyPasswordResetCode({ code, auth }: VerifyPasswordResetCodeProps): Promise<string> {
  return await fbVerifyPasswordResetCode(auth, code);
}

export async function confirmPasswordReset({ auth, oobCode, newPassword }: ConfirmPasswordResetProps): Promise<void> {
  return await fbConfirmPasswordReset(auth, oobCode, newPassword);
}
